import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { XCircle, Calendar, Link2 } from "lucide-react";

// Local types to match API response
interface SubscriptionOrder {
  id: string;
  name: string;
  email: string;
}

interface SubscriptionWithOrder {
  id: string;
  preOrderId: string;
  status: string | null;
  monthlyAmount: number | null;
  nextBillingDate: Date | string | null;
  stripeSubscriptionId: string | null;
  createdAt: Date | string | null;
  cancelledAt: Date | string | null;
  order?: SubscriptionOrder;
}

const statusColors: Record<string, string> = {
  active: "bg-green-100 text-green-800 border-green-300",
  cancelled: "bg-red-100 text-red-800 border-red-300",
  paused: "bg-yellow-100 text-yellow-800 border-yellow-300",
};

function formatDate(date: Date | string | null): string {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatCurrency(amount: number | null): string {
  if (amount === null) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

export function SubscriptionsList() {
  const { toast } = useToast();

  const { data: subscriptions, isLoading } = useQuery<SubscriptionWithOrder[]>({
    queryKey: ["/api/admin/subscriptions"],
  });

  const cancelMutation = useMutation({
    mutationFn: async (subscriptionId: string) => {
      return apiRequest("POST", `/api/admin/subscriptions/${subscriptionId}/cancel`);
    },
    onSuccess: () => {
      toast({ title: "Subscription cancelled", description: "The subscription has been cancelled." });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/subscriptions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48 mb-6" />
        <Skeleton className="h-64 w-full" />
      </Card>
    );
  }

  return (
    <Card className="p-6" data-testid="subscriptions-list">
      <h3 className="text-lg font-semibold mb-6">Insurance Subscriptions</h3>

      {!subscriptions || subscriptions.length === 0 ? (
        <p className="text-muted-foreground text-center py-8" data-testid="no-subscriptions">No subscriptions found</p>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="subscriptions-table">
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Linked Order</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Monthly Amount</TableHead>
                <TableHead>Next Billing</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptions.map((subscription) => (
                <TableRow key={subscription.id} data-testid={`subscription-row-${subscription.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium" data-testid={`subscription-name-${subscription.id}`}>{subscription.order?.name || "-"}</p>
                      <p className="text-sm text-muted-foreground" data-testid={`subscription-email-${subscription.id}`}>
                        {subscription.order?.email || "-"}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {subscription.order ? (
                      <div className="flex items-center gap-2">
                        <Link2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-mono text-sm">
                          {subscription.preOrderId.slice(0, 8)}...
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={statusColors[subscription.status || "active"]}
                      data-testid={`subscription-status-${subscription.id}`}
                    >
                      {subscription.status || "active"}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatCurrency(subscription.monthlyAmount)}</TableCell>
                  <TableCell>
                    {subscription.nextBillingDate ? (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        {formatDate(subscription.nextBillingDate)}
                      </div>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>{formatDate(subscription.createdAt)}</TableCell>
                  <TableCell>
                    {subscription.status === "active" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            data-testid={`btn-cancel-subscription-${subscription.id}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Cancel
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Cancel Subscription</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to cancel this subscription? This action cannot be
                              undone. The customer will no longer have terrarium insurance coverage.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => cancelMutation.mutate(subscription.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Cancel Subscription
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                    {subscription.status === "cancelled" && (
                      <span className="text-sm text-muted-foreground">
                        Cancelled {formatDate(subscription.cancelledAt)}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
